import type { QueryClient } from "@tanstack/react-query";
import { apiClient } from "./client";
import { HEADER_QK, type DataFreshness } from "./headerApi";
import { HOME_QK, type PinnedTiles, type StartPage } from "./homeApi";
import { NOTIFY_QK, type NotificationCount } from "./notificationCentreApi";
import { PLANNER_COCKPIT_QK, type GssUpdateCount } from "./plannerCockpitApi";
import { QK } from "./summitOpsApi";
import type { TickerDto } from "./tickerApi";
import type { Me, OperationsHub } from "./types";
import { setFeatureSwitch } from "../lib/featureSwitches";
import { PLANNER_COCKPIT_FEATURE, VIPER_REBALANCE_FEATURE } from "../lib/release160Switches";
import { PLANNING_CALENDAR_FEATURE } from "../pages/planning/calendar/calendarSwitch";

// Release 1.9.0 (W0.3, O-R143, D-O187): one start-up request (`GET /api/v1/bootstrap`) that returns /me, data
// freshness, unread notification count, ticker, start page, pinned tiles, Operations Hub (when landing on Home or Hub)
// and feature switches in a single round trip. Hydrates React Query and feature switches so the shell and start page
// render without a waterfall; falls back to `GET /api/v1/me` and the individual endpoints if `/bootstrap` is unavailable.

export interface BootstrapFeatureSwitches {
  planningCalendarEnabled: boolean;
  viperRebalanceEnabled: boolean;
  plannerCockpitEnabled: boolean;
  valueKind: string;
}

export interface BootstrapPayload {
  me: Me;
  freshness: DataFreshness | null;
  notificationCount: NotificationCount | null;
  ticker: TickerDto | null;
  startPage: StartPage | null;
  pinnedTiles: PinnedTiles | null;
  operationsHub: OperationsHub | null;
  featureSwitches: BootstrapFeatureSwitches | null;
  gssUpdateCount: GssUpdateCount | null;
  dataVersion: string;
  generatedAtUtc: string;
  valueKind: string;
}

let bootstrappedTicker: { scopeKey: string; data: TickerDto } | null = null;
let bootstrappedSwitchesHydrated = false;

export function consumeBootstrappedTicker(scopeKey: string): TickerDto | null {
  if (bootstrappedTicker && bootstrappedTicker.scopeKey === scopeKey) {
    const data = bootstrappedTicker.data;
    bootstrappedTicker = null;
    return data;
  }
  return null;
}

export function areSwitchesBootstrapped(): boolean {
  return bootstrappedSwitchesHydrated;
}

export function resetBootstrapState(): void {
  bootstrappedTicker = null;
  bootstrappedSwitchesHydrated = false;
}

export function applyBootstrapPayload(queryClient: QueryClient, payload: BootstrapPayload): Me {
  const me = payload.me;
  queryClient.setQueryData([...QK.me], me);

  if (payload.freshness) {
    queryClient.setQueryData([...HEADER_QK.freshness], payload.freshness);
  }
  if (payload.notificationCount) {
    queryClient.setQueryData([...NOTIFY_QK.count], payload.notificationCount);
  }
  if (payload.startPage) {
    queryClient.setQueryData([...HOME_QK.start], payload.startPage);
  }
  if (payload.pinnedTiles) {
    queryClient.setQueryData([...HOME_QK.tiles], payload.pinnedTiles);
  }
  if (payload.operationsHub) {
    queryClient.setQueryData([...QK.operationsHub], payload.operationsHub);
  }
  if (payload.gssUpdateCount) {
    queryClient.setQueryData([...PLANNER_COCKPIT_QK.count], payload.gssUpdateCount);
  }
  if (payload.featureSwitches) {
    setFeatureSwitch(PLANNING_CALENDAR_FEATURE, payload.featureSwitches.planningCalendarEnabled === true);
    setFeatureSwitch(VIPER_REBALANCE_FEATURE, payload.featureSwitches.viperRebalanceEnabled === true);
    setFeatureSwitch(PLANNER_COCKPIT_FEATURE, payload.featureSwitches.plannerCockpitEnabled === true);
    bootstrappedSwitchesHydrated = true;
  }
  if (payload.ticker) {
    const scopeKey = `${me.userPrincipalName ?? "anon"}:${(me.roles ?? []).join(",")}`;
    bootstrappedTicker = { scopeKey, data: payload.ticker };
  }

  return me;
}

export const bootstrapApi = {
  getBootstrap: async (): Promise<BootstrapPayload> => (await apiClient.get<BootstrapPayload>("/bootstrap")).data,

  /**
   * Fetches `/bootstrap` and hydrates the shell caches; falls back to `getMeFallback` (`/me`) if `/bootstrap` is
   * unavailable or mocked out in older tests.
   */
  resolveMeWithBootstrap: async (queryClient: QueryClient, getMeFallback: () => Promise<Me>): Promise<Me> => {
    try {
      const payload = await bootstrapApi.getBootstrap();
      if (payload && payload.me && Array.isArray(payload.me.roles)) {
        return applyBootstrapPayload(queryClient, payload);
      }
    } catch {
      // Fall back to /me below (and let individual components fetch their own endpoints).
    }
    return getMeFallback();
  }
};
